import { useState } from 'react';
import { Filter, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { categorias, etiquetas, presentaciones } from '@/data/products';

interface ProductFiltersProps {
  filters: {
    categorias: string[];
    etiquetas: string[];
    presentaciones: string[];
    ordenar: string;
  };
  onFiltersChange: (filters: any) => void;
  isOpen: boolean;
  onToggle: () => void;
}

export function ProductFilters({ filters, onFiltersChange, isOpen, onToggle }: ProductFiltersProps) {
  const updateFilter = (type: string, value: string, checked: boolean) => {
    const currentValues = filters[type as keyof typeof filters] as string[];
    let newValues;

    if (checked) {
      newValues = [...currentValues, value];
    } else {
      newValues = currentValues.filter(v => v !== value);
    }

    onFiltersChange({
      ...filters,
      [type]: newValues
    });
  };

  const clearFilters = () => {
    onFiltersChange({
      categorias: [],
      etiquetas: [],
      presentaciones: [],
      ordenar: 'relevancia'
    });
  };

  const hasActiveFilters = 
    filters.categorias.length > 0 || 
    filters.etiquetas.length > 0 || 
    filters.presentaciones.length > 0;

  return (
    <>
      {/* Mobile Filter Toggle */}
      <div className="lg:hidden mb-4">
        <Button 
          variant="outline" 
          onClick={onToggle}
          className="w-full"
        >
          <Filter className="w-4 h-4 mr-2" />
          Filtros
          {hasActiveFilters && (
            <span className="ml-2 bg-primary text-primary-foreground text-xs rounded-full px-2 py-0.5">
              {filters.categorias.length + filters.etiquetas.length + filters.presentaciones.length}
            </span>
          )}
        </Button>
      </div>

      {/* Sidebar Filters */}
      <div className={`
        fixed lg:static inset-0 z-40 lg:z-0
        ${isOpen ? 'block' : 'hidden lg:block'}
      `}>
        {/* Mobile Overlay */}
        <div 
          className="lg:hidden absolute inset-0 bg-black/50"
          onClick={onToggle}
        />

        {/* Filter Panel */}
        <div className="
          absolute lg:relative right-0 lg:right-auto top-0 h-full w-80 lg:w-full max-w-sm lg:max-w-none
          bg-card border-l lg:border-l-0 lg:border-r border-border
          overflow-y-auto lg:overflow-visible
          shadow-xl lg:shadow-none
        ">
          <div className="p-4 lg:p-0">
            {/* Mobile Header */}
            <div className="lg:hidden flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold">Filtros</h3>
              <Button variant="ghost" size="sm" onClick={onToggle}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            <div className="space-y-6">
              {/* Sort */}
              <div>
                <Label className="text-sm font-medium mb-3 block">Ordenar por</Label>
                <Select 
                  value={filters.ordenar} 
                  onValueChange={(value) => onFiltersChange({ ...filters, ordenar: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="relevancia">Relevancia</SelectItem>
                    <SelectItem value="precio-asc">Precio: Menor a Mayor</SelectItem>
                    <SelectItem value="precio-desc">Precio: Mayor a Menor</SelectItem>
                    <SelectItem value="nombre">Nombre A-Z</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Categories */}
              <div>
                <Label className="text-sm font-medium mb-3 block">Categoría</Label>
                <div className="space-y-2">
                  {categorias.map((categoria) => (
                    <div key={categoria} className="flex items-center space-x-2">
                      <Checkbox
                        id={`cat-${categoria}`}
                        checked={filters.categorias.includes(categoria)}
                        onCheckedChange={(checked) => 
                          updateFilter('categorias', categoria, checked as boolean)
                        }
                      />
                      <Label 
                        htmlFor={`cat-${categoria}`}
                        className="text-sm cursor-pointer"
                      >
                        {categoria}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Presentations */}
              <div>
                <Label className="text-sm font-medium mb-3 block">Presentación</Label>
                <div className="space-y-2">
                  {presentaciones.map((presentacion) => (
                    <div key={presentacion} className="flex items-center space-x-2">
                      <Checkbox
                        id={`pres-${presentacion}`}
                        checked={filters.presentaciones.includes(presentacion)}
                        onCheckedChange={(checked) => 
                          updateFilter('presentaciones', presentacion, checked as boolean)
                        }
                      />
                      <Label 
                        htmlFor={`pres-${presentacion}`}
                        className="text-sm cursor-pointer"
                      >
                        {presentacion}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tags */}
              <div>
                <Label className="text-sm font-medium mb-3 block">Etiquetas</Label>
                <div className="space-y-2">
                  {etiquetas.map((etiqueta) => (
                    <div key={etiqueta} className="flex items-center space-x-2">
                      <Checkbox
                        id={`tag-${etiqueta}`}
                        checked={filters.etiquetas.includes(etiqueta)}
                        onCheckedChange={(checked) => 
                          updateFilter('etiquetas', etiqueta, checked as boolean)
                        }
                      />
                      <Label 
                        htmlFor={`tag-${etiqueta}`}
                        className="text-sm cursor-pointer capitalize"
                      >
                        {etiqueta}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button 
                  variant="outline" 
                  onClick={clearFilters}
                  className="w-full"
                >
                  Limpiar Filtros
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}